import { useState, type RefObject } from 'react'
import { useToast } from '../../platform/feedback/ToastProvider'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { DeleteTransactionsError, useDeleteTransaction, useDeleteTransactions } from './deleteTransactions'

const plural = (n: number) => `${n} transaction${n === 1 ? '' : 's'}`

/** What a failed delete tells the user, by what actually happened to the rows. */
export function deleteFailureMessage(thrown: unknown, scope: 'one' | 'bulk'): string {
  const e = thrown instanceof DeleteTransactionsError ? thrown : undefined
  if (e !== undefined && e.deletedBefore > 0) {
    return `${plural(e.deletedBefore)} ${e.deletedBefore === 1 ? 'was' : 'were'} deleted, then the rest failed. ${plural(e.remainingIds.length)} left — delete again to retry only those.`
  }
  if (e?.status === 404) {
    return scope === 'one'
      ? 'This transaction no longer exists, so there was nothing to delete.'
      : 'Nothing was deleted: at least one of these transactions no longer exists. Refresh the list and select again.'
  }
  if (e?.status === 422) return 'The request was rejected, and nothing was deleted. This is a bug on our side — please try again, and contact support if it keeps happening.'
  return "The delete didn't go through, and nothing was deleted. Try again."
}

/**
 * Confirm, then delete (#799). One id uses `DELETE /transactions/{id}`, several use the chunked bulk
 * call. `onDeleted` fires when at least one row is gone — including a run that failed part-way — so
 * the screen can drop them from its selection or leave a detail page that no longer exists.
 */
export function DeleteTransactionsDialog({
  ids: requested,
  onClose,
  onDeleted,
  restoreFocusTo,
}: {
  ids: readonly string[]
  onClose: () => void
  onDeleted: () => void
  /** Where focus goes on close when the opener is gone (a toolbar whose selection just emptied). */
  restoreFocusTo?: RefObject<HTMLElement | null>
}) {
  const one = useDeleteTransaction()
  const many = useDeleteTransactions()
  const toast = useToast()
  // After a partial failure the dialog narrows to the ids that did not land, so a retry never resends deleted ones.
  const [remaining, setRemaining] = useState<readonly string[] | null>(null)
  const ids = remaining ?? requested
  const single = ids.length === 1
  const scope = single ? 'one' : 'bulk'

  return (
    <ConfirmDialog
      open
      mode="delete_transactions"
      title={single ? 'Delete this transaction?' : `Delete ${plural(ids.length)}?`}
      body={`${single ? '1 transaction' : `${plural(ids.length)}`} will be deleted and removed from your transactions, budgets and reports. Undo isn't available.`}
      confirmLabel={single ? 'Delete transaction' : `Delete ${plural(ids.length)}`}
      pendingLabel="Deleting…"
      destructive
      restoreFocusTo={restoreFocusTo}
      onCancel={onClose}
      errorFor={(thrown) => {
        // Rows from earlier chunks are gone: the screen must hear about them even though the dialog stays open.
        if (thrown instanceof DeleteTransactionsError && thrown.deletedBefore > 0) {
          setRemaining(thrown.remainingIds)
          onDeleted()
        }
        return deleteFailureMessage(thrown, scope)
      }}
      onConfirm={async () => {
        if (single) {
          await one.mutateAsync(ids[0] ?? '')
          toast.show({ kind: 'success', message: 'Transaction deleted' })
        } else {
          const out = await many.mutateAsync(ids)
          toast.show({ kind: 'success', message: `${plural(out.deleted)} deleted` })
        }
        onDeleted()
        onClose()
      }}
    />
  )
}
